import { darfErledigen, markierTag, type TaskStatus } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { cn } from '../../lib/cn'
import { heuteSchluessel, useMarkieren } from '../../lib/markierung'
import { useStatusSetzen } from '../../lib/mutationen'
import { useFaeden, useFadenSetzen, type Tageslage } from '../../lib/mywork'
import { termineAmTagQuery } from '../../lib/queries'
import { AufgabenVerweis } from '../AufgabeKontext'
import { Wiedersehen } from '../meintag/Wiedersehen'
import { OffeneEnden } from './OffeneEnden'

/**
 * **Feierabend – Tag abschließen** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md)
 * AP 24.27). Vier Schritte, etwa zwei Minuten; danach hat alles Offene einen Platz (Masicampo &
 * Baumeister: ein Plan beendet das gedankliche Nachhaken, nicht erst das Erledigen).
 *
 * 1. **Nicht fertig geworden** – je Aufgabe ein Tipp: nächster Werktag · später (Wiedersehen) ·
 *    lassen; dazu der Faden für morgen.
 * 2. **Offene Enden** – dieselbe Karte wie im Tag.
 * 3. **Der nächste Arbeitstag** – Termine und was schon vorgenommen ist.
 * 4. **Heute** – was geschafft ist; „Feierabend" beendet einen laufenden Puls.
 *
 * Nichts geschieht von selbst: ohne Tipp bleibt eine Markierung, wie sie ist, und rückt morgen als
 * „liegengeblieben" nach oben (F-95).
 */

export interface FeierabendAufgabe {
  id: string
  title: string
  status: TaskStatus
  abnahmeDurchId: string | null
  /** Der wirksame Bearbeiter – wer bearbeitet hat, nimmt nicht ab (`darfErledigen`). */
  assigneeId: string | null
}

const FERTIG_SCHLUESSEL = 'netcon-work:feierabend'

/** Heute schon Feierabend gemacht? Je Gerät – reine Anzeige. */
export function feierabendGemacht(): boolean {
  try {
    return window.localStorage.getItem(FERTIG_SCHLUESSEL) === heuteSchluessel()
  } catch {
    return false
  }
}

export function Feierabend({
  unfertig,
  ichId,
  erledigtHeute,
  imPulsHeute,
  lage,
  pulsLaeuft,
  pulsBeenden,
  schliessen,
  vorbereiten,
}: {
  unfertig: readonly FeierabendAufgabe[]
  ichId: string | null
  erledigtHeute: number
  /** An wie vielen Dingen heute im Puls gearbeitet wurde. */
  imPulsHeute: number
  lage: Pick<Tageslage, 'zettelOhneMappe' | 'aufnahmenOhneTermin'> | undefined
  pulsLaeuft: boolean
  pulsBeenden: () => void
  schliessen: () => void
  /** Tipp auf einen Termin des nächsten Tages: Vorbereitung ansehen oder anstoßen. */
  vorbereiten: (t: { titel: string; beginn: string }) => void
}) {
  const heute = heuteSchluessel()
  const naechster = markierTag('morgen', heute)
  const markieren = useMarkieren()
  const faeden = useFaeden()
  const fadenSetzen = useFadenSetzen()
  const { data: morgen } = useQuery(termineAmTagQuery(naechster))
  const status = useStatusSetzen()
  const [entschieden, setEntschieden] = useState<
    Record<string, 'erledigt' | 'morgen' | 'spaeter' | 'lassen'>
  >({})
  const [spaeterFuer, setSpaeterFuer] = useState<FeierabendAufgabe | null>(null)

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !spaeterFuer) schliessen()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [schliessen, spaeterFuer])

  const naechsterName = new Date(`${naechster}T12:00:00`).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  const termineMorgen = (morgen?.termine ?? [])
    .filter((t) => !t.abgesagt && !t.ganztaegig)
    .sort((a, b) => a.beginn.localeCompare(b.beginn))
  /** Beginnt ein Termin schon vorher (Urlaub ab Freitag 16 Uhr), steht „seit Fr" statt der Uhrzeit. */
  const beginnText = (beginn: string) =>
    beginn.slice(0, 10) < naechster
      ? `seit ${new Date(`${beginn.slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short' })}`
      : beginn.slice(11, 16)

  const waehlen = (a: FeierabendAufgabe, wie: 'erledigt' | 'morgen' | 'spaeter' | 'lassen') => {
    if (wie === 'spaeter') return setSpaeterFuer(a)
    if (wie === 'erledigt') {
      /** Erledigt – oder zur Abnahme, dieselbe Regel wie überall (`darfErledigen`). */
      setEntschieden((alt) => ({ ...alt, [a.id]: 'erledigt' }))
      status.mutate({ id: a.id, status: darfErledigen(a, ichId) ? 'erledigt' : 'review' })
      return
    }
    setEntschieden((alt) => ({ ...alt, [a.id]: wie }))
    markieren.mutate({ taskIds: [a.id], am: wie === 'morgen' ? naechster : null })
  }

  const fertig = () => {
    if (pulsLaeuft) pulsBeenden()
    try {
      window.localStorage.setItem(FERTIG_SCHLUESSEL, heute)
    } catch {
      // nur Anzeige
    }
    schliessen()
  }

  const schritt = (n: number, titel: string, zusatz?: string) => (
    <div className="mb-2.5 flex items-baseline gap-2.5">
      <span className="flex size-[26px] shrink-0 items-center justify-center self-center rounded-full bg-[#f1effb] text-[13px] font-extrabold text-[#4a3aa7]">
        {n}
      </span>
      <h3 className="text-[16px] font-semibold">{titel}</h3>
      {zusatz && <span className="text-[12.5px] text-ink2">{zusatz}</span>}
    </div>
  )

  return (
    <div
      className="unter-leiste fixed inset-x-0 bottom-0 z-[45] overflow-y-auto bg-page"
      role="dialog"
      aria-modal="true"
      aria-label="Tag abschließen"
    >
      <div className="mx-auto flex max-w-[880px] flex-col gap-4 px-4 pt-6 pb-[calc(32px+env(safe-area-inset-bottom))]">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[13px] text-ink2">
              {new Date().toLocaleDateString('de-DE', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </div>
            <h2 className="text-[26px] font-bold">Tag abschließen</h2>
            <p className="text-[14px] text-[#3d3b37]">
              Vier Schritte, etwa zwei Minuten. Danach hat alles Offene einen Platz.
            </p>
          </div>
          <button
            type="button"
            onClick={schliessen}
            className="min-h-11 cursor-pointer px-2 text-[14px] font-semibold text-accent-deep"
          >
            zurück zum Tag
          </button>
        </header>

        <section className="rounded-card border border-line bg-surface px-4 py-4 shadow-task">
          {schritt(1, `Nicht fertig geworden · ${unfertig.length}`, 'je ein Tipp')}
          {unfertig.length === 0 && (
            <p className="text-[13.5px] text-ink2">Alles Vorgenommene ist erledigt.</p>
          )}
          <ul className="flex flex-col gap-3">
            {unfertig.map((a) => {
              const wahl = entschieden[a.id]
              const f = faeden.get(a.id)
              return (
                <li
                  key={a.id}
                  className="flex flex-col gap-2 rounded-xl border border-line px-3.5 py-3"
                >
                  <AufgabenVerweis
                    aufgabeId={a.id}
                    className="text-[15px] font-semibold hover:text-accent-deep"
                  >
                    {a.title}
                  </AufgabenVerweis>
                  <div
                    role="group"
                    aria-label={`Wann weiter: ${a.title}`}
                    className="flex flex-wrap gap-1.5"
                  >
                    {(
                      [
                        ['erledigt', darfErledigen(a, ichId) ? '✓ Erledigt' : '✓ Zur Abnahme'],
                        [
                          'morgen',
                          `☀ ${new Date(`${naechster}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'long' })}`,
                        ],
                        ['spaeter', 'später …'],
                        ['lassen', 'lassen'],
                      ] as const
                    ).map(([wie, text]) => (
                      <button
                        key={wie}
                        type="button"
                        aria-pressed={wahl === wie}
                        onClick={() => waehlen(a, wie)}
                        className={cn(
                          'min-h-9 cursor-pointer rounded-lg border px-3 text-[13px] font-semibold',
                          wahl === wie
                            ? 'border-accent-deep bg-accent-deep text-white'
                            : 'border-line2 bg-surface text-ink',
                        )}
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                  {a.status === 'in_arbeit' && wahl !== 'lassen' && wahl !== 'erledigt' && (
                    <label className="flex flex-col gap-0.5 rounded-[10px] border border-[#dcefe5] bg-[#f7fbf9] px-3 py-1.5">
                      <span className="text-[10.5px] font-bold tracking-[0.08em] text-good-text uppercase">
                        Faden – nächster Schritt
                      </span>
                      <input
                        defaultValue={f?.naechsterSchritt ?? ''}
                        maxLength={300}
                        placeholder="Wo machst du morgen weiter?"
                        onBlur={(e) => {
                          const neu = e.target.value.trim()
                          if (neu !== (f?.naechsterSchritt ?? ''))
                            fadenSetzen.mutate({
                              taskId: a.id,
                              stand: f?.stand ?? '',
                              naechsterSchritt: neu,
                            })
                        }}
                        className="border-0 bg-transparent text-[15px] outline-none"
                      />
                    </label>
                  )}
                </li>
              )
            })}
          </ul>
        </section>

        <section className="rounded-card border border-line bg-surface px-4 py-4 shadow-task">
          {schritt(2, 'Offene Enden', 'aus Terminen und Notizen')}
          <OffeneEnden lage={lage} kompakt />
        </section>

        <section className="rounded-card border border-line bg-surface px-4 py-4 shadow-task">
          {schritt(3, naechsterName)}
          {termineMorgen.length === 0 ? (
            <p className="text-[13.5px] text-ink2">Keine Termine – ein Tag für Tiefes.</p>
          ) : (
            <ul className="flex flex-col gap-1 text-[13.5px]">
              {termineMorgen.slice(0, 8).map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => vorbereiten({ titel: t.betreff, beginn: t.beginn })}
                    className="flex min-h-10 w-full cursor-pointer items-center gap-3 rounded-lg px-1 text-left hover:bg-[#faf9f6]"
                  >
                    <span className="tabular w-[44px] shrink-0 text-ink2">
                      {beginnText(t.beginn)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{t.betreff}</span>
                    <span className="shrink-0 text-[12.5px] font-semibold text-accent-deep">
                      vorbereiten ›
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[12.5px] text-ink2">
            {new Date().getHours() < 18
              ? 'Vorbereitungen für Runden mit Verlauf entstehen heute um 18 Uhr von selbst – oder jetzt über „vorbereiten".'
              : 'Tipp auf einen Termin: die Vorbereitung vom Vorabend ansehen oder jetzt erstellen.'}
          </p>
        </section>

        <section className="flex flex-col gap-3 rounded-card border border-[#dcd6f4] bg-[#f1effb] px-4 py-4">
          {schritt(4, 'Heute')}
          <p className="text-[14px] text-[#3d3770]">
            {erledigtHeute} erledigt
            {imPulsHeute > 0
              ? ` · im Puls an ${imPulsHeute} ${imPulsHeute === 1 ? 'Ding' : 'Dingen'}`
              : ''}
            {pulsLaeuft ? ' · der Puls läuft noch und endet mit dem Feierabend' : ''}
          </p>
          <button
            type="button"
            onClick={fertig}
            className="min-h-12 cursor-pointer rounded-[14px] bg-[#4a3aa7] text-[16px] font-bold text-white"
          >
            Feierabend
          </button>
        </section>
      </div>

      {spaeterFuer && (
        <Wiedersehen
          aufgabeId={spaeterFuer.id}
          titel={spaeterFuer.title}
          schliessen={() => setSpaeterFuer(null)}
          weg={() => {
            setEntschieden((alt) => ({ ...alt, [spaeterFuer.id]: 'spaeter' }))
            markieren.mutate({ taskIds: [spaeterFuer.id], am: null })
          }}
        />
      )}
    </div>
  )
}
